import { Scanner } from "@yudiel/react-qr-scanner"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useEffect } from "react"
import { useBarcodeScanner } from "src/providers/BarcodeScannerProvider"

import Button from "src/components/Button"

import useWindowSize from "src/hooks/use_window_size"

import useCameraPermission from "./useCameraPermission"

export const INSTRUCTION_TEXT = {
  QR_SECTION:
    "Hold customer's QR Code in view of the camera or scan with connected barcode scanner",
  BARCODE_SECTION_TITLE: "Scan with barcode scanner",
  BARCODE_SECTION:
    "Use a connected barcode scanner to scan customer''s QR code on their Dockwa app",
  CAMERA_SECTION_TITLE: "Or enable camera access",
  CAMERA_SECTION:
    "Enable camera access in settings to scan QR codes directly from this device",
}

export const SMALL_MOBILE_HEIGHT_PX = 640
export const MEDIUM_MOBILE_HEIGHT_PX = 840

const QRScanner = ({
  onScan,
  onError,
  aspectRatio = "1/1",
  fullScreenMobile = false,
  isLoadingScanResult = false,
  scanResultLoadingText,
}) => {
  const { permissionState, requestCameraPermission, canRequestPermission } =
    useCameraPermission()

  const { scannedBarcode, setScannedBarcode } = useBarcodeScanner()

  const { width, height } = useWindowSize()

  useEffect(() => {
    // If the user scanned a QR with a barcode scanner, treat it like a camera QR scan
    if (scannedBarcode?.length > 0) {
      onScan(scannedBarcode)
      setScannedBarcode(null)
    }
  }, [onScan, scannedBarcode, setScannedBarcode])

  const handleScan = (scanResult) => {
    const result = scanResult[0]?.rawValue

    return onScan(result)
  }

  const renderRequestPermissionButton = () => {
    return canRequestPermission() ? (
      <Button variant="tertiary" onClick={requestCameraPermission}>
        Enable Camera
      </Button>
    ) : null
  }

  const renderNoCameraAccess = () => (
    <div
      data-testid="qr-scanner-no-camera-access-instructions"
      className="rounded border-2 border-gray-300 bg-gray-100"
    >
      <div className="flex flex-row space-x-4 px-4">
        <div className="my-4 flex-1 px-4">
          <div className="flex flex-col items-center space-y-1 py-6 text-center">
            <i className="icon icon-barcode-scanner text-xl text-gray-500" />
            <div className="font-semibold">
              {INSTRUCTION_TEXT.BARCODE_SECTION_TITLE}
            </div>
            <div>{INSTRUCTION_TEXT.BARCODE_SECTION}</div>
          </div>
        </div>
        <div className="my-4 flex-1 border-l-2 border-gray-300 px-4">
          <div className="flex flex-col items-center space-y-1 py-6 text-center">
            <i className="icon icon-no-camera text-xl text-gray-500" />
            <div className="font-semibold">
              {INSTRUCTION_TEXT.CAMERA_SECTION_TITLE}
            </div>
            <div>{INSTRUCTION_TEXT.CAMERA_SECTION}</div>
            {renderRequestPermissionButton()}
          </div>
        </div>
      </div>
    </div>
  )

  const renderNoCameraAccessMobile = () => (
    <div
      data-testid="qr-scanner-no-camera-access-instructions-mobile"
      className="flex h-full flex-col space-y-4 px-4 pt-24"
    >
      <div className="rounded border border-2 border-gray-300 bg-gray-100 px-4 text-lg">
        <div className="flex flex-col items-center space-y-1 py-6 text-center">
          <i className="icon icon-barcode-scanner text-2xl text-gray-500" />
          <div className="font-semibold">
            {INSTRUCTION_TEXT.BARCODE_SECTION_TITLE}
          </div>
          <div>{INSTRUCTION_TEXT.BARCODE_SECTION}</div>
        </div>
      </div>
      <div className="px-4 text-lg">
        <div className="flex flex-col items-center space-y-1 py-6 text-center">
          <i className="icon icon-no-camera text-2xl text-gray-500" />
          <div className="font-semibold">
            {INSTRUCTION_TEXT.CAMERA_SECTION_TITLE}
          </div>
          <div>{INSTRUCTION_TEXT.CAMERA_SECTION}</div>
          {renderRequestPermissionButton()}
        </div>
      </div>
      <div className="flex w-full flex-grow flex-row items-center justify-center space-x-2 pb-4">
        <span className="icon icon-spinner icon-spin" />
        <span>
          {isLoadingScanResult ? scanResultLoadingText : "Awaiting Scan..."}
        </span>
      </div>
    </div>
  )

  const renderOverlay = () => (
    <>
      <div
        className="absolute inset-0 flex items-start justify-center pt-6"
        data-testid="qr-scanner-custom-finder"
      >
        <div className="aspect-square h-3/4 rounded border-2 border-white shadow-[0_0_100px_250px_rgba(0,0,0,0.25)]" />
      </div>
      <div
        className="absolute bottom-0 mb-2 w-full"
        data-testid="qr-scanner-instructional-text"
      >
        <div className="flex w-full items-center justify-center">
          <div className="z-max mx-0 w-2/3 rounded bg-black bg-opacity-50 p-2 text-center font-semibold text-white">
            {INSTRUCTION_TEXT.QR_SECTION}
          </div>
        </div>
      </div>
    </>
  )

  const renderOverlayMobile = () => (
    <div
      className="absolute inset-0"
      data-testid="qr-scanner-custom-finder-mobile"
    >
      <div className="mt-24 flex flex-col items-center justify-center space-y-4">
        <div
          className={classNames(
            "rounded border-2 border-white shadow-[0_0_100px_250px_rgba(0,0,0,0.25)]",
            {
              "size-32": height < SMALL_MOBILE_HEIGHT_PX,
              "size-40":
                height >= SMALL_MOBILE_HEIGHT_PX &&
                height < MEDIUM_MOBILE_HEIGHT_PX,
              "aspect-square w-2/3": height >= MEDIUM_MOBILE_HEIGHT_PX,
            }
          )}
        />
        <div
          className="flex w-full items-center justify-center"
          data-testid="qr-scanner-instructional-text-mobile"
        >
          <div
            className={classNames(
              "z-max rounded bg-black bg-opacity-50 text-center font-semibold text-white",
              {
                "mx-2 w-full p-2 text-xs": height < SMALL_MOBILE_HEIGHT_PX,
                "mx-2 w-full p-2 text-base":
                  height >= SMALL_MOBILE_HEIGHT_PX &&
                  height < MEDIUM_MOBILE_HEIGHT_PX,
                "w-2/3 p-4 text-lg": height >= MEDIUM_MOBILE_HEIGHT_PX,
              }
            )}
          >
            {INSTRUCTION_TEXT.QR_SECTION}
          </div>
        </div>
        {renderMobileSpinner()}
      </div>
    </div>
  )

  const renderMobileSpinner = () => (
    <div
      className="flex w-full flex-grow flex-row items-center justify-center space-x-2 pt-4"
      data-testid="qr-scanner-spinner-mobile"
    >
      <div className="min-w-1/3 flex max-w-3/4 items-center justify-center space-x-2 rounded bg-black bg-opacity-50 p-2 text-center font-semibold text-white">
        <i className="icon icon-spinner icon-spin" />
        <span>
          {isLoadingScanResult
            ? scanResultLoadingText || "Loading result..."
            : "Awaiting Scan..."}
        </span>
      </div>
    </div>
  )

  const renderScanner = () => (
    <>
      <div
        className={classNames("relative overflow-hidden", {
          rounded: !fullScreenMobile,
        })}
      >
        <Scanner
          onScan={handleScan}
          onError={onError}
          sound={false}
          components={{
            finder: false,
          }}
          styles={{
            container: {
              aspectRatio: fullScreenMobile
                ? `${width}/${height}`
                : aspectRatio,
              height: "100%",
              width: "100%",
            },
          }}
          constraints={{
            facingMode: "environment",
            width: { min: 320, ideal: 720, max: 1920 },
            height: { min: 200, ideal: 720, max: 1080 },
          }}
        />
        {fullScreenMobile ? renderOverlayMobile() : renderOverlay()}
      </div>
    </>
  )

  const renderPermissionRequest = () =>
    fullScreenMobile ? renderNoCameraAccessMobile() : renderNoCameraAccess()

  return permissionState === "granted"
    ? renderScanner()
    : renderPermissionRequest()
}

QRScanner.propTypes = {
  onScan: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  aspectRatio: PropTypes.string,
  fullScreenMobile: PropTypes.bool,
  isLoadingScanResult: PropTypes.bool,
  scanResultLoadingText: PropTypes.string,
}
export default QRScanner
